import { Box, Chip, Paper, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import type { ISkillReferenceDTO } from '@sharedInterfaces/ISkill';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import { Sparkles } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import ItemBadge from '../../ItemBadge/ItemBadge';

interface SuperPowerItemProps {
    skill: ISkillReferenceDTO;
    isFocusPower?: boolean;
    onClick?: () => void;
}

function SuperPowerItem({ skill, isFocusPower = false, onClick }: SuperPowerItemProps) {
    const theme = useTheme();
    const navigate = useNavigate();

    const getLevelColor = (level: number) => {
        if (isFocusPower) return theme.palette.accent?.main || theme.palette.primary.main;
        return theme.palette.levels?.[level as 1 | 2 | 3 | 4 | 5] || theme.palette.primary.main;
    };

    const handleClick = () => {
        if (onClick) {
            onClick();
        } else {
            navigate(`/${ELinks.SKILLS}/${skill.id}`);
        }
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                borderRadius: 2,
                background: theme.palette.background.paper,
                border: `1px solid ${alpha(getLevelColor(skill.level), 0.2)}`,
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minHeight: 80,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${alpha(getLevelColor(skill.level), 0.3)}`,
                    borderColor: getLevelColor(skill.level),
                    bgcolor: alpha(getLevelColor(skill.level), 0.05),
                },
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    bottom: 0,
                    width: '4px',
                    background: getLevelColor(skill.level),
                },
            }}
            onClick={handleClick}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ color: getLevelColor(skill.level) }}>
                        <Sparkles size={20} />
                    </Box>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 600,
                            fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                        }}
                    >
                        {skill.title}
                    </Typography>
                    {isFocusPower && (
                        <Chip
                            label="Focus Power"
                            size="small"
                            sx={{
                                bgcolor: alpha(theme.palette.accent?.main || theme.palette.primary.main, 0.2),
                                color: theme.palette.accent?.main || theme.palette.primary.main,
                                fontSize: '0.6rem',
                                height: 20,
                                fontWeight: 600,
                            }}
                        />
                    )}
                </Box>

                <ItemBadge
                    value={skill.level}
                    title={`Level ${skill.level}`}
                    icon={getAppIcon(EIconCategory.LEVEL)}
                    level={skill.level}
                    iconColor={isFocusPower ? theme.palette.accent?.main || theme.palette.primary.main : undefined}
                    style={
                        isFocusPower
                            ? {
                                  backgroundColor: alpha(theme.palette.accent?.main || theme.palette.primary.main, 0.2),
                                  color: theme.palette.accent?.main || theme.palette.primary.main,
                                  border: `1px solid ${alpha(theme.palette.accent?.main || theme.palette.primary.main, 0.3)}`,
                              }
                            : undefined
                    }
                />
            </Box>
        </Paper>
    );
}

export default SuperPowerItem;
